  // Control para omitir el siguiente refresh automático (no utilizado actualmente)
  // const skipNextRefresh = useRef(false)
import { useState, useEffect, useCallback, useRef } from "react"
import type { DynamicUser, DynamicSchema, TableConfig, DynamicUpdatePayload } from "../types/dynamic"
import { fetchDynamicData } from "../services/api"
import { 
  getDynamicUsersFromDB, 
  mergeDynamicUsersToDb, 
  getDynamicSchemaFromDB, 
  saveDynamicSchemaToDb,
  cleanTemporaryDuplicates,
  getQueueFromDB
} from "../services/database"
import { SchemaService } from "../services/schema"
import { useNetworkStatus } from "./useNetworkStatus"

export const useDynamicUsers = () => {
  const [users, setUsers] = useState<DynamicUser[]>([])
  const [schema, setSchema] = useState<DynamicSchema>({})
  const [config, setConfig] = useState<TableConfig>({ visibleFields: [], editableFields: [] })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isOnline = useNetworkStatus()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const loadingRef = useRef(false)
  const initialLoadRef = useRef(false)
  const cancelCurrentFetchRef = useRef(false)
  const isQueueProcessingRef = useRef(false)
  const isApiOperationActiveRef = useRef(false)
  const skipNextAutoRefreshRef = useRef(false)
  const autoRefreshSkippedRef = useRef(false)

  // Helper function to sort users by ID (descending)
  const sortUsers = (usersArray: DynamicUser[]) => {
    return usersArray.sort((a, b) => b.id - a.id)
  }

  const cancelCurrentFetch = useCallback(() => {
    cancelCurrentFetchRef.current = true
    console.log("🚫 Cancelando descarga actual de usuarios dinámicos")
  }, [])

  // Function to notify when queue processing starts
  const setQueueProcessing = useCallback((processing: boolean) => {
    isQueueProcessingRef.current = processing
    if (processing) {
      console.log("🔄 Cola en procesamiento - pausando actualizaciones automáticas de usuarios dinámicos")
      cancelCurrentFetch()
    } else {
      console.log("✅ Cola completada - reanudando actualizaciones automáticas de usuarios dinámicos")
    }
  }, [cancelCurrentFetch])

  // Function to notify when API operations start/end
  const setApiOperationActive = useCallback((active: boolean) => {
    isApiOperationActiveRef.current = active
    if (active) {
      console.log("🌐 Operación API activa - pausando actualizaciones automáticas de usuarios dinámicos")
      cancelCurrentFetch()
    } else {
      console.log("✅ Operación API completada - reanudando actualizaciones automáticas de usuarios dinámicos")
    }
  }, [cancelCurrentFetch])

  const refreshData = useCallback(
    async (force = false, skipOptimisticCheck = false) => {
      // Prevent multiple simultaneous loads
      if (loadingRef.current && !force) {
        console.log("⏸️ Carga de usuarios dinámicos ya en progreso")
        return
      }

      // Reset cancel flag
      cancelCurrentFetchRef.current = false
      loadingRef.current = true
      setLoading(true)
      setError(null)

      try {
        // Always try to load local data first
        const [localUsers, localSchema] = await Promise.all([
          getDynamicUsersFromDB(),
          getDynamicSchemaFromDB()
        ])

        // Solo cargar datos locales en carga inicial o si no hay usuarios en estado
        if (!skipOptimisticCheck && (localUsers.length > 0 && localSchema) && users.length === 0) {
          const sortedLocalUsers = sortUsers(localUsers)
          const processedConfig = SchemaService.processSchema(localSchema)
          
          setUsers(sortedLocalUsers)
          setSchema(localSchema)
          setConfig(processedConfig)
          
          console.log(`📱 ${sortedLocalUsers.length} usuarios dinámicos cargados localmente (carga inicial)`)

          // Mark initial load as complete if we have local data
          if (!initialLoadRef.current) {
            initialLoadRef.current = true
            setLoading(false)
            loadingRef.current = false
          }
        }

        // Check if fetch was cancelled before proceeding
        if (cancelCurrentFetchRef.current) {
          console.log("🚫 Descarga cancelada antes de obtener datos del servidor dinámico")
          setLoading(false)
          loadingRef.current = false
          return
        }

        // Then try to fetch from server if online
        if (isOnline) {
          console.log("🔄 Obteniendo datos dinámicos del servidor...")

          // Check again if cancelled during fetch
          if (cancelCurrentFetchRef.current) {
            console.log("🚫 Descarga cancelada durante la obtención del servidor dinámico")
            setLoading(false)
            loadingRef.current = false
            return
          }

          try {
            const serverResponse = await fetchDynamicData()
            const { users: serverUsers, schema: serverSchema } = serverResponse.data

            // Final check if cancelled after fetch
            if (cancelCurrentFetchRef.current) {
              console.log("🚫 Descarga cancelada después de obtener datos del servidor dinámico")
              setLoading(false)
              loadingRef.current = false
              return
            }

            // MERGE INTELIGENTE POR CAMPO ÚNICO (FIXED)
            // Previene duplicados identificando usuarios por campo único en lugar de solo ID
            // Use current users in state (includes optimistic updates) instead of only localUsers
            const currentUsers = skipOptimisticCheck ? localUsers : users
            const mergedUsers = SchemaService.mergeUsersByUniqueField(currentUsers, serverUsers, serverSchema)
            const processedConfig = SchemaService.processSchema(serverSchema)

            // Solo actualizar si hay cambios significativos
            const hasChanges = SchemaService.hasSignificantDataChanges(users, mergedUsers) || 
                               JSON.stringify(serverSchema) !== JSON.stringify(schema)

            if (hasChanges) {
              setUsers(mergedUsers)
              setSchema(serverSchema)
              setConfig(processedConfig)
              
              // Limpiar registros temporales que ya tienen versión en servidor
              const temporaryUsersToClean = SchemaService.getTemporaryUsersToClean(currentUsers, serverUsers, serverSchema)
              if (temporaryUsersToClean.length > 0) {
                console.log(`🧹 Limpiando ${temporaryUsersToClean.length} registros temporales duplicados`)
                await cleanTemporaryDuplicates(temporaryUsersToClean)
              }
              
              // Usuarios con cambios en cola: su copia local no se pisa hasta que se envíen
              const pendingUniqueValues = new Set(
                (await getQueueFromDB())
                  .filter(item => item.type === "update_dynamic_user")
                  .map(item => String((item.data as DynamicUpdatePayload).uniqueFieldValue))
              )
              const hasPendingChanges = (serverUser: DynamicUser) => {
                const uniqueValue = SchemaService.getUniqueFieldValue(serverUser, serverSchema)
                return uniqueValue !== null && pendingUniqueValues.has(uniqueValue)
              }

              // Hacer merge en base de datos sin duplicados
              await Promise.all([
                mergeDynamicUsersToDb(serverUsers, hasPendingChanges), // Gana el servidor salvo cambios en cola
                saveDynamicSchemaToDb(serverSchema)
              ])
              
              const localOnlyUsers = SchemaService.getLocalOnlyUsers(currentUsers, serverUsers)
              console.log(`✅ Merge por campo único completado: ${mergedUsers.length} usuarios totales (${localOnlyUsers.length} solo locales + ${serverUsers.length} del servidor)`)
            } else {
              console.log("ℹ️ No hay cambios significativos en los datos dinámicos")
            }
          } catch (serverError) {
            console.warn("⚠️ Error cargando datos dinámicos del servidor:", serverError)
            // If server fails but we have local data, that's OK
            if (localUsers.length === 0 && !localSchema) {
              throw serverError
            }
          }
        } else if ((localUsers.length === 0 || !localSchema) && users.length === 0) {
          // Only show offline message if no local data exists and no current users
          console.log("📱 Sin conexión y sin datos dinámicos locales")
          setError("Sin conexión a internet y no hay datos dinámicos guardados localmente")
        }
      } catch (err) {
        // Check if error occurred due to cancellation
        if (cancelCurrentFetchRef.current) {
          console.log("🚫 Error ignorado debido a cancelación de usuarios dinámicos")
          setLoading(false)
          loadingRef.current = false
          return
        }

        console.error("❌ Error cargando usuarios dinámicos:", err)

        // If server fails, try local data as fallback (if not already loaded)
        if (users.length === 0) {
          try {
            const [localUsers, localSchema] = await Promise.all([
              getDynamicUsersFromDB(),
              getDynamicSchemaFromDB()
            ])
            
            if (localUsers.length > 0 && localSchema) {
              const sortedLocalUsers = sortUsers(localUsers)
              const processedConfig = SchemaService.processSchema(localSchema)
              
              setUsers(sortedLocalUsers)
              setSchema(localSchema)
              setConfig(processedConfig)
              
              console.log(`📱 Fallback: ${sortedLocalUsers.length} usuarios dinámicos cargados localmente`)
              setError("Datos cargados desde almacenamiento local (sin conexión)")
            } else {
              setError(err instanceof Error ? err.message : "Error desconocido cargando datos dinámicos")
            }
          } catch (localErr) {
            console.error("❌ Error cargando datos dinámicos locales:", localErr)
            setError("Error cargando datos dinámicos")
          }
        }
      } finally {
        setLoading(false)
        loadingRef.current = false
        initialLoadRef.current = true
      }
    },
    [isOnline, schema],
  )

  // Clear interval when component unmounts
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  // Load data on mount and when network status changes ONLY
  useEffect(() => {
    if (skipNextAutoRefreshRef.current) {
      skipNextAutoRefreshRef.current = false
      console.log("⏸️ Saltando auto-refresh después de actualización optimista")
      return
    }
    refreshData()
  }, [refreshData])



  // Effect to restart interval when API operations complete
  useEffect(() => {
    if (!isOnline || !initialLoadRef.current) return
    
    if (!isApiOperationActiveRef.current && !intervalRef.current) {
      console.log("🔄 Reiniciando intervalo después de operación API dinámicos")
      startAutoRefresh()
    }
  }, [isApiOperationActiveRef.current, isOnline, initialLoadRef.current])

  const startAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (!isOnline || !initialLoadRef.current) {
      console.log("📴 Sin conexión o carga inicial - pausando actualización automática de usuarios dinámicos")
      return
    }

    console.log("⏳ Iniciando intervalo de actualización automática de usuarios dinámicos")
    intervalRef.current = setInterval(() => {
      // Un alta, edición o impresión local deja la cancelación puesta y solo refreshData la quitaba,
      // así que el intervalo se detenía para siempre: se salta un ciclo y se reanuda en el siguiente.
      // Nunca dos seguidos: con un cambio por minuto (kiosko o mesa con fila) no llegarían los de otros equipos
      if (cancelCurrentFetchRef.current && !loadingRef.current) {
        cancelCurrentFetchRef.current = false
        if (!autoRefreshSkippedRef.current) {
          autoRefreshSkippedRef.current = true
          console.log("⏸️ Actualización automática omitida un ciclo - cambio local reciente (dinámicos)")
          return
        }
        console.log("⏭️ Cambio local reciente, pero ya se omitió el ciclo anterior - se actualiza (dinámicos)")
      }
      if (!loadingRef.current && !cancelCurrentFetchRef.current && !isQueueProcessingRef.current && !isApiOperationActiveRef.current) {
        autoRefreshSkippedRef.current = false
        console.log("⏰ Actualización automática de datos dinámicos")
        refreshData()
      } else if (isQueueProcessingRef.current) {
        console.log("⏸️ Actualización automática pausada - cola en procesamiento (dinámicos)")
      } else if (isApiOperationActiveRef.current) {
        console.log("⏸️ Actualización automática pausada - operación API activa (dinámicos)")
      }
    }, 60000)

    return () => {
      if (intervalRef.current) {
        console.log("🧹 Limpiando intervalo de actualización de usuarios dinámicos")
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isOnline, refreshData])

  // Auto-refresh every 60 seconds when online (but not on initial load)
  useEffect(() => {
    startAutoRefresh()
  }, [startAutoRefresh])

  // Helper function to get field value from user
  const getFieldValue = useCallback((user: DynamicUser, fieldName: string) => {
    return SchemaService.getFieldValue(user, fieldName)
  }, [])

  // Helper function to format field value
  const formatFieldValue = useCallback((value: any, fieldName: string) => {
    const fieldConfig = schema[fieldName]
    if (!fieldConfig) return value?.toString() || "-"
    return SchemaService.formatFieldValue(value, fieldConfig)
  }, [schema])

  // Filter users based on search term
  const filterUsers = useCallback((searchTerm: string) => {
    return SchemaService.filterUsers(users, config, searchTerm)
  }, [users, config])

  // Function to add user optimistically
  const addUserOptimistically = useCallback((newUser: DynamicUser) => {
    console.log(`🚀 Agregando usuario optimistamente: ID ${newUser.id}`)
    setUsers(prev => [newUser, ...prev])
    
    // Mark to skip next auto-refresh to preserve optimistic update
    skipNextAutoRefreshRef.current = true
    
    return newUser
  }, [])

  // Function to update user optimistically
  const updateUserOptimistically = useCallback((updatedUser: DynamicUser) => {
    console.log(`🔄 Actualizando usuario optimistamente: ID ${updatedUser.id}`)
    setUsers(prev => prev.map(user => 
      user.id === updatedUser.id ? updatedUser : user
    ))
    
    // Mark to skip next auto-refresh to preserve optimistic update
    skipNextAutoRefreshRef.current = true
    
    return updatedUser
  }, [])

  // Function to replace a temporary user (optimistic create) with the server version (real ID and folio)
  const replaceUserOptimistically = useCallback((tempId: number, realUser: DynamicUser) => {
    console.log(`🔄 Reemplazando usuario temporal: ID ${tempId} → ${realUser.id}`)
    setUsers(prev => {
      // Si un refresh ya trajo al usuario real, solo quitar el temporal
      if (prev.some(user => user.id === realUser.id)) {
        return prev.filter(user => user.id !== tempId)
      }
      if (prev.some(user => user.id === tempId)) {
        return prev.map(user => user.id === tempId ? realUser : user)
      }
      return [realUser, ...prev]
    })

    // Mark to skip next auto-refresh to preserve optimistic update
    skipNextAutoRefreshRef.current = true

    return realUser
  }, [])

  return {
    users,
    schema,
    config,
    loading,
    error,
    refreshData,
    cancelCurrentFetch,
    setQueueProcessing,
    setApiOperationActive,
    getFieldValue,
    formatFieldValue,
    filterUsers,
    addUserOptimistically,
    updateUserOptimistically,
    replaceUserOptimistically,
  }
}
